import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

/**
 * Página de Gestión de Permisos por Usuario
 * Permite asignar roles por módulo a un usuario específico
 */
export default function UserPermissions() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Definición de módulos y roles disponibles
  const modules = [
    {
      name: 'calendario',
      displayName: 'Calendario Hebreo',
      roles: [
        { value: 'calendarizador', label: 'Calendarizador', description: 'Puede crear y gestionar eventos, lecturas y citas' }
      ]
    },
    {
      name: 'posts',
      displayName: 'Posts y Categorías',
      roles: [
        { value: 'posteador', label: 'Posteador', description: 'Puede crear, editar y publicar posts' }
      ]
    },
    {
      name: 'historicidad',
      displayName: 'Historicidad y Recursos',
      roles: [
        { value: 'historiador', label: 'Historiador', description: 'Puede crear borradores y publicar directamente' },
        { value: 'gestor', label: 'Gestor', description: 'Gestiona datos en estado inicial' },
        { value: 'validador', label: 'Validador', description: 'Valida información' },
        { value: 'editor', label: 'Editor', description: 'Contrasta y aprueba información' }
      ]
    },
    {
      name: 'biblia',
      displayName: 'Biblia y Strongs',
      roles: [
        { value: 'lector', label: 'Lector', description: 'Puede crear notas, resaltados y marcadores bíblicos' }
      ]
    },
    {
      name: 'mitzvot',
      displayName: 'Mitzvot (Mandamientos)',
      roles: [
        { value: 'investigador', label: 'Investigador', description: 'Puede crear perspectivas personalizadas de mitzvot' }
      ]
    }
  ];

  useEffect(() => {
    loadUserAndPermissions();
  }, [userId]);

  const loadUserAndPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar información del usuario
      const userResponse = await api.get(`/admin/users/${userId}`);
      if (userResponse.data.success) {
        setUser(userResponse.data.data);
      }

      // Cargar permisos del usuario
      const permissionsResponse = await api.get(`/admin/users/${userId}/permissions`);
      if (permissionsResponse.data.success) {
        // Convertir array a objeto por moduleName
        // Para historicidad, permitir múltiples roles (array)
        // Para otros módulos, mantener un solo permiso (objeto)
        const permsObj = {};
        permissionsResponse.data.data.forEach((perm) => {
          if (perm.moduleName === 'historicidad') {
            // Para historicidad, almacenar como array
            if (!permsObj[perm.moduleName]) {
              permsObj[perm.moduleName] = [];
            }
            permsObj[perm.moduleName].push(perm);
          } else {
            // Para otros módulos, almacenar como objeto único
            permsObj[perm.moduleName] = perm;
          }
        });
        setPermissions(permsObj);
      }
    } catch (err) {
      console.error('Error loading user/permissions:', err);
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermission = async (moduleName, role) => {
    try {
      setSaving(true);

      const data = {
        moduleName,
        roleName: role,
        isActive: true
      };

      const response = await api.post(`/admin/users/${userId}/permissions`, data);

      if (response.data.success) {
        // Recargar permisos
        await loadUserAndPermissions();
        alert('Permiso asignado exitosamente');
      } else {
        alert('Error al asignar permiso');
      }
    } catch (err) {
      console.error('Error saving permission:', err);
      alert(err.response?.data?.message || 'Error al asignar permiso');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePermission = async (permissionId) => {
    if (!confirm('¿Estás seguro de eliminar este permiso?')) {
      return;
    }

    try {
      setSaving(true);

      const response = await api.delete(`/admin/users/${userId}/permissions/${permissionId}`);

      if (response.data.success) {
        // Recargar permisos
        await loadUserAndPermissions();
        alert('Permiso eliminado exitosamente');
      } else {
        alert('Error al eliminar permiso');
      }
    } catch (err) {
      console.error('Error removing permission:', err);
      alert(err.response?.data?.message || 'Error al eliminar permiso');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async (newRole) => {
    const roleNames = {
      'administrador': 'Administrador',
      'usuario_registrado': 'Usuario Registrado'
    };

    if (!confirm(`¿Estás seguro de cambiar el rol base a "${roleNames[newRole]}"?\n\nEsto afectará los permisos generales del usuario.`)) {
      return;
    }

    try {
      setSaving(true);

      const response = await api.put(`/admin/users/${userId}`, {
        rol: newRole
      });

      if (response.data.success) {
        // Recargar usuario
        await loadUserAndPermissions();
        alert(`Rol cambiado a ${roleNames[newRole]} exitosamente`);
      } else {
        alert('Error al cambiar rol');
      }
    } catch (err) {
      console.error('Error changing role:', err);
      alert(err.response?.data?.message || 'Error al cambiar rol');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error || 'Usuario no encontrado'}
        </div>
        <button
          onClick={() => navigate('/admin/users')}
          className="mt-4 text-blue-600 hover:text-blue-900"
        >
          ← Volver a Gestión de Usuarios
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/users')}
          className="text-blue-600 hover:text-blue-900 mb-4 flex items-center"
        >
          ← Volver a Gestión de Usuarios
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Permisos por Módulo</h1>
        <div className="flex items-center gap-4">
          <div>
            <p className="text-lg text-gray-600">{user.nombre}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
            user.rol === 'administrador'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {user.rol === 'administrador' ? 'Administrador' : 'Usuario Registrado'}
          </span>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">ℹ️ Información sobre Permisos</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Asigna roles específicos por módulo para habilitar funcionalidades avanzadas</li>
          <li>• Los usuarios registrados pueden ver contenido público sin permisos adicionales</li>
          <li>• Solo los administradores pueden gestionar permisos</li>
        </ul>
      </div>

      {/* Cambiar Rol Base */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">👤 Rol Base del Usuario</h2>
        <p className="text-sm text-gray-600 mb-4">
          El rol base determina los permisos generales del usuario en todo el sistema.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Usuario Registrado */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              user.rol === 'usuario_registrado'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => user.rol !== 'usuario_registrado' && handleChangeRole('usuario_registrado')}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Usuario Registrado</h3>
              {user.rol === 'usuario_registrado' && (
                <span className="text-blue-600">✓</span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Puede crear contenido local, enviar a revisión y acceder a funciones básicas.
            </p>
          </div>

          {/* Administrador */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              user.rol === 'administrador'
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => user.rol !== 'administrador' && handleChangeRole('administrador')}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Administrador</h3>
              {user.rol === 'administrador' && (
                <span className="text-purple-600">✓</span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Acceso total al sistema, gestión de usuarios, configuración y todos los módulos.
            </p>
          </div>
        </div>

        {user.rol === 'administrador' && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Nota:</strong> Los administradores tienen acceso completo a todos los módulos automáticamente.
            </p>
          </div>
        )}
      </div>

      {/* Módulos y Roles */}
      <div className="space-y-6">
        {modules.map((module) => {
          const currentPermission = permissions[module.name];
          const isHistoricidad = module.name === 'historicidad';
          
          // Para historicidad, currentPermission es un array
          // Para otros módulos, es un objeto o undefined
          const assignedRoles = isHistoricidad && Array.isArray(currentPermission)
            ? currentPermission.map(p => p.roleName)
            : currentPermission
            ? [currentPermission.roleName]
            : [];

          return (
            <div key={module.name} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Header del Módulo */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {module.displayName}
                </h2>
                {isHistoricidad && assignedRoles.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {assignedRoles.length} {assignedRoles.length === 1 ? 'rol asignado' : 'roles asignados'}
                  </p>
                )}
              </div>

              {/* Roles Disponibles */}
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-3">
                  {isHistoricidad 
                    ? 'Selecciona uno o más roles para asignar (puedes asignar múltiples roles):' 
                    : 'Selecciona un rol para asignar:'}
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {module.roles.map((role) => {
                    const isAssigned = assignedRoles.includes(role.value);
                    
                    // Para historicidad, encontrar el permiso específico para eliminar
                    const permissionToRemove = isHistoricidad && isAssigned && Array.isArray(currentPermission)
                      ? currentPermission.find(p => p.roleName === role.value)
                      : currentPermission;
                    
                    return (
                      <div
                        key={role.value}
                        className={`relative p-4 border-2 rounded-lg transition-all ${
                          isAssigned
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                        }`}
                        onClick={() => !isAssigned && handleSavePermission(module.name, role.value)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {isAssigned && (
                                <span className="text-green-600 font-bold">✓</span>
                              )}
                              <div className={`font-medium ${isAssigned ? 'text-green-900' : 'text-gray-900'}`}>
                                {role.label}
                              </div>
                            </div>
                            <div className={`text-sm ${isAssigned ? 'text-green-700' : 'text-gray-600'}`}>
                              {role.description}
                            </div>
                            {isAssigned && (
                              <div className="mt-2">
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  Rol Asignado
                                </span>
                              </div>
                            )}
                          </div>
                          {isAssigned && permissionToRemove && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemovePermission(permissionToRemove.id);
                              }}
                              disabled={saving}
                              className="ml-4 text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Botón de Volver */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => navigate('/admin/users')}
          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          ← Volver a Gestión de Usuarios
        </button>
      </div>
    </div>
  );
}





